/**
 * PromiseSendEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides promise-specific
 * props (source type, linked invoice IDs for invoice attachment fetching).
 *
 * Fetches linked transaction IDs internally via GraphQL rather than
 * relying on page-level data (which may not include invoice details).
 *
 * When the company is linked to Dimensions, attachments are fetched from
 * the Document API (ADM) instead of the Financial API.
 */

import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { type EmailAttachment, SingleEmailModal } from '@/components/shared/single-email-modal';
import { useAdmDocumentDownloadAsBase64 } from '@/hooks/useAdmDocumentDownloadAsBase64';
import { useCompanyProductType } from '@/hooks/useCompanyProductType';
import { useLogPromiseEmail } from '@/hooks/useLogPromiseEmail';
import { usePromiseTransactionsPaginatedGraphQL } from '@/hooks/usePromiseTransactionsGraphQL';
import { useTransactionDocumentDownload } from '@/hooks/useTransactionDocumentDownload';
import { fetchAdmDocumentsForTransaction } from '@/hooks/useTransactionDocuments';
import {
  attachmentErrorMessages,
  attachmentsError,
  emptyAttachmentsResult,
  type FetchAttachmentsResult,
  fetchAttachmentsWithProgress,
} from '@/lib/fetch-attachments-utils';
import { trackError } from '@/lib/telemetry';
import { useAuth } from '@/providers/AuthProvider';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import { useMode } from '@/providers/ModeProvider';

export interface PromiseSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promiseId: string;
  customerId: string;
  customerName?: string;
  accountNumber?: string;
  onSuccess?: () => void;
}

export function PromiseSendEmailModal({
  open,
  onOpenChange,
  promiseId,
  customerId,
  customerName = '',
  accountNumber = '',
  onSuccess,
}: Readonly<PromiseSendEmailModalProps>) {
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const { downloadAdmDocumentAsBase64 } = useAdmDocumentDownloadAsBase64();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();
  const { isDemo } = useMode();
  const { isDimensions } = useCompanyProductType();
  const logPromiseEmail = useLogPromiseEmail();

  const { transactions: promiseTransactions } = usePromiseTransactionsPaginatedGraphQL(open ? promiseId : undefined, {
    pageSize: 500,
  });

  const linkedTransactionIds = useMemo(
    () => promiseTransactions.map((t) => t.id).filter((id) => id > 0),
    [promiseTransactions],
  );

  const handleFetchAttachments = useCallback(
    async (onProgress: (done: number, total: number) => void): Promise<FetchAttachmentsResult> => {
      if (linkedTransactionIds.length === 0) return emptyAttachmentsResult();
      if (isDemo) return emptyAttachmentsResult();

      if (isDimensions) {
        const txsWithExternalId = promiseTransactions.filter((t) => t.id > 0 && t.externalId);
        if (txsWithExternalId.length === 0) return emptyAttachmentsResult();

        if (!getAccessToken) {
          return attachmentsError(attachmentErrorMessages.noAuthentication);
        }

        const ctx = await getDocumentContext();
        if (!ctx) {
          return attachmentsError(attachmentErrorMessages.noDocumentService);
        }

        onProgress(0, txsWithExternalId.length);

        const allAttachments: EmailAttachment[] = [];
        let completed = 0;
        let foundDocCount = 0;
        let failedDocCount = 0;

        const results = await Promise.allSettled(
          txsWithExternalId.map(async (tx) => {
            const docs = await fetchAdmDocumentsForTransaction(ctx, getAccessToken, tx.externalId!, 10);

            const downloaded = await Promise.allSettled(
              docs.map((doc) => downloadAdmDocumentAsBase64(doc.id, doc.fileName, doc.fileType)),
            );

            const successful = downloaded
              .filter((r): r is PromiseFulfilledResult<EmailAttachment> => r.status === 'fulfilled')
              .map((r) => r.value);

            foundDocCount += docs.length;
            failedDocCount += downloaded.length - successful.length;

            completed++;
            onProgress(completed, txsWithExternalId.length);

            return successful;
          }),
        );

        let failedTxCount = 0;
        for (const result of results) {
          if (result.status === 'fulfilled') {
            allAttachments.push(...result.value);
          } else {
            failedTxCount++;
          }
        }

        if (allAttachments.length === 0 && foundDocCount === 0 && failedTxCount > 0) {
          return attachmentsError(attachmentErrorMessages.documentLookupFailed(failedTxCount));
        }

        // A rejected transaction lookup is counted as one found-but-failed document
        // so the caller can distinguish "nothing to attach" from "everything failed".
        return {
          attachments: allAttachments,
          foundCount: foundDocCount + failedTxCount,
          failedCount: failedDocCount + failedTxCount,
        };
      }

      return fetchAttachmentsWithProgress(linkedTransactionIds, downloadDocumentAsBase64, onProgress);
    },
    [
      linkedTransactionIds,
      promiseTransactions,
      isDemo,
      isDimensions,
      downloadDocumentAsBase64,
      downloadAdmDocumentAsBase64,
      getDocumentContext,
      getAccessToken,
    ],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
      attachedTransactionIds?: number[];
    }) => {
      logPromiseEmail.mutate(
        {
          sourceId: promiseId,
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
          attachedTransactionIds: payload.attachedTransactionIds,
        },
        {
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error('Failed to save promise email to history'));
            toast.error('Email was sent but failed to save to history. Please contact support if the issue persists.');
          },
        },
      );
    },
    [logPromiseEmail, promiseId],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="promise"
      sourceId={Number(promiseId)}
      transactionIds={linkedTransactionIds}
      callbackEventName="creditcontrol:promise-email-done"
      attachLabel="Attach outstanding invoices"
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={['promises', 'general']}
      allowedTemplateTypes={['email']}
    />
  );
}
